import { useState } from "react";
import { sha3_512 } from "js-sha3";
import * as ed from "@noble/ed25519";

// Helper for hex string to Uint8Array (Buffer replacement for browser)
function hexToBytes(hex) {
    if (typeof hex !== "string") {
        throw new Error("Input must be a hex string");
    }
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < bytes.length; i++) {
        bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
    }
    return bytes;
}

export default function V2Audit() {
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    async function handleFile(file) {
        setResult(null);
        setError(null);

        try {
            const text = await file.text();
            let cert;
            try {
                cert = JSON.parse(text);
            } catch (e) {
                throw new Error("Invalid JSON file");
            }

            const { meta, scope, payload, integrity, archive_signature, archive_public_key } = cert;

            if (!meta || !scope || !payload || !integrity) {
                throw new Error("Malformed certificate structure");
            }

            // --- Rebuild core_data (Canonical JSON) ---
            const coreData = {
                meta,
                scope,
                payload,
            };

            // Canonical JSON: Sort keys recursively or use a library.
            // Requirement mentions: JSON.stringify(coreData, Object.keys(coreData).sort())
            // This sorts top-level keys: meta, payload, scope.
            // WARNING: If backend uses deep sort, this might fail for nested objects.
            // Provided code used this, so we stick to it.
            const canonical = JSON.stringify(coreData, Object.keys(coreData).sort());

            // --- Verify Archive Hash ---
            const recomputedHash = sha3_512(canonical);

            if (recomputedHash !== integrity.archive_hash) {
                console.error("Hash Mismatch:", {
                    expected: integrity.archive_hash,
                    computed: recomputedHash,
                    canonicalSource: canonical
                });
                throw new Error("Archive hash mismatch");
            }

            // --- Verify Signature ---
            const pubKey = hexToBytes(archive_public_key);
            const sig = hexToBytes(archive_signature);
            const msg = hexToBytes(integrity.archive_hash);

            const validSig = await ed.verify(sig, msg, pubKey);

            if (!validSig) {
                throw new Error("Invalid signature");
            }

            // --- Verify Chain Head ---
            if (!payload || payload.length === 0) {
                throw new Error("Empty payload");
            }
            const last = payload[payload.length - 1];
            if (last.event_hash !== integrity.chain_head) {
                throw new Error("Chain head mismatch");
            }

            // --- Verify Internal Chain ---
            for (let i = 1; i < payload.length; i++) {
                if (payload[i].prev_event_hash !== payload[i - 1].event_hash) {
                    throw new Error("Chain linking invalid at index " + i);
                }
            }

            setResult("VALID");
        } catch (e) {
            console.error(e);
            setError(e.message);
        }
    }

    function handleDrop(e) {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file) handleFile(file);
    }

    return (
        <div style={{ color: "#fff", fontFamily: "'Inter', sans-serif" }}>
            <h2 style={{ color: "#00FF94", letterSpacing: 1 }}>SENTINEL AUDIT LAB</h2>
            <div style={{ opacity: 0.6, marginBottom: 40, fontSize: "0.9rem" }}>
                Client-side Cryptographic Verification Engine. No external data telemetry.
            </div>

            <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                style={{
                    marginTop: 40,
                    padding: "80px 40px",
                    border: `2px dashed ${result === "VALID" ? "#00FF94" : "#00E0FF"}`,
                    borderRadius: 24,
                    textAlign: "center",
                    cursor: "pointer",
                    background: result === "VALID" ? "rgba(0, 255, 148, 0.05)" : "rgba(0, 224, 255, 0.03)",
                    backdropFilter: "blur(10px)",
                    transition: "all 0.4s ease",
                    boxShadow: result === "VALID" ? "0 0 30px rgba(0, 255, 148, 0.1)" : "none",
                    position: "relative",
                    overflow: "hidden"
                }}
            >
                {/* Background scanning effect if idle */}
                {!result && !error && (
                    <div style={{
                        position: "absolute",
                        top: 0, left: "-100%", width: "100%", height: "100%",
                        background: "linear-gradient(90deg, transparent, rgba(0, 224, 255, 0.05), transparent)",
                        animation: "sentinel-scan 3s infinite linear"
                    }} />
                )}

                <div style={{ fontSize: 40, marginBottom: 20 }}>{result === "VALID" ? "🛡️" : "📂"}</div>
                <div style={{ fontSize: 24, marginBottom: 10, fontWeight: "bold" }}>
                    {result === "VALID" ? "INTEGRITY VERIFIED" : "DRAG & DROP .ECP FILE"}
                </div>
                <div style={{ opacity: 0.5, fontSize: "0.9rem" }}>
                    {result === "VALID" ? "SHA3-512 + Ed25519 Checks Passed" : "Secure localized proof verification"}
                </div>
            </div>

            <style>{`
                @keyframes sentinel-scan {
                    0% { left: -100%; }
                    100% { left: 100%; }
                }
            `}</style>

            {result && (
                <div style={{
                    marginTop: 40, color: "#00FF94", fontSize: 28, fontWeight: "bold", textAlign: "center",
                    textShadow: "0 0 20px rgba(0, 255, 148, 0.5)", animation: "sentinel-verify 0.5s ease-out"
                }}>
                    CERTIFICATE AUTHENTICATED ✅
                </div>
            )}

            {error && (
                <div style={{
                    marginTop: 40, color: "#FF003C", fontSize: 24, fontWeight: "bold", textAlign: "center",
                    textShadow: "0 0 20px rgba(255, 0, 60, 0.3)"
                }}>
                    VERIFICATION FAILED ❌ — {error}
                </div>
            )}
        </div>
    );
}
